import React from 'react';
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input
} from 'reactstrap';
import ReactLoader from 'components/Loader';
import { FaMicrophone, FaVolumeUp, FaSync } from 'react-icons/fa';
import useAudioDevices from '../../../../../hooks/useAudioDevices';
import { FlexChip } from 'components/FlexChip';

const AudioDeviceSelector = ({ isOpen, toggle, onDeviceChange }) => {
  const {
    audioInputDevices,
    audioOutputDevices,
    selectedInputDevice,
    selectedOutputDevice,
    inputVolume,
    outputVolume,
    selectInputDevice,
    selectOutputDevice,
    setInputVolumeLevel,
    setOutputVolumeLevel,
    isSupported,
    isLoading,
    error,
    refreshDevices
  } = useAudioDevices();

  const handleInputDeviceChange = (event) => {
    const deviceId = event.target.value;
    selectInputDevice(deviceId);

    if (onDeviceChange) onDeviceChange('input', deviceId);
  };

  const handleOutputDeviceChange = (event) => {
    const deviceId = event.target.value;
    selectOutputDevice(deviceId);

    if (onDeviceChange) onDeviceChange('output', deviceId);
  };

  const handleInputVolumeChange = (event) => {
    const volume = parseFloat(event.target.value);
    setInputVolumeLevel(volume);

    if (onDeviceChange) onDeviceChange('inputVolume', null, volume);
  };

  const handleOutputVolumeChange = (event) => {
    const volume = parseFloat(event.target.value);
    setOutputVolumeLevel(volume);

    if (onDeviceChange) onDeviceChange('outputVolume', null, volume);
  };

  const handleRefresh = () => {
    refreshDevices();
  };

  if (!isSupported) {
    return (
      <Modal isOpen={isOpen} toggle={toggle} size="lg">
        <ModalHeader toggle={toggle}>Recurso indisponível</ModalHeader>
        <ModalBody>
          <FlexChip
            type="danger"
            text="Seu navegador não suporta o recurso de seleção de dispositivos de áudio.
            Recomendamos usar uma versão do Chrome, Firefox ou Edge mais
            recentes."
          />
        </ModalBody>
        <ModalFooter>
          <Button color="danger" onClick={toggle}>
            Fechar
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Dispositivos de comunicação</ModalHeader>
      <ModalBody>
        {error && <FlexChip type="danger" text={'Erro:' + error} />}

        {isLoading ? (
          <div className="d-flex justify-content-center">
            <ReactLoader width="80" />
          </div>
        ) : (
          <div className="d-flex flex-column" style={{ gap: '1.5rem' }}>
            {/* Dispositivos lado a lado */}
            <div className="row">
              <div className="col-md-6">
                <FormGroup className="mb-0">
                  <Label
                    for="inputDevice"
                    className="d-flex align-items-center mb-2"
                    style={{ gap: '8px' }}
                  >
                    <FaMicrophone />
                    <span>Dispositivo de entrada</span>
                  </Label>
                  <Input
                    type="select"
                    id="inputDevice"
                    value={selectedInputDevice}
                    onChange={handleInputDeviceChange}
                    disabled={audioInputDevices.length === 0}
                  >
                    {audioInputDevices.length === 0 ? (
                      <option value="">Nenhum microfone detectado</option>
                    ) : (
                      audioInputDevices.map((device) => (
                        <option key={device.deviceId} value={device.deviceId}>
                          {device.label}
                        </option>
                      ))
                    )}
                  </Input>
                </FormGroup>
              </div>

              <div className="col-md-6">
                <FormGroup className="mb-0">
                  <Label
                    for="outputDevice"
                    className="d-flex align-items-center mb-2"
                    style={{ gap: '8px' }}
                  >
                    <FaVolumeUp />
                    <span>Dispositivo de saída</span>
                  </Label>
                  <Input
                    type="select"
                    id="outputDevice"
                    value={selectedOutputDevice}
                    onChange={handleOutputDeviceChange}
                    disabled={audioOutputDevices.length === 0}
                  >
                    {audioOutputDevices.length === 0 ? (
                      <option value="">Nenhum alto-falante detectado</option>
                    ) : (
                      audioOutputDevices.map((device) => (
                        <option key={device.deviceId} value={device.deviceId}>
                          {device.label}
                        </option>
                      ))
                    )}
                  </Input>
                </FormGroup>
              </div>
            </div>

            {/* Controles de volume lado a lado */}
            <div className="row">
              <div className="col-md-6">
                <div className="mt-3">
                  <Label
                    for="inputVolume"
                    className="d-flex justify-content-between align-items-center"
                  >
                    <span>Volume de entrada</span>
                    <span className="text-muted">
                      {Math.round(inputVolume * 100)}%
                    </span>
                  </Label>
                  <Input
                    type="range"
                    id="inputVolume"
                    min="0"
                    max="1"
                    step="0.01"
                    value={inputVolume}
                    onChange={handleInputVolumeChange}
                    className="form-range"
                  />
                </div>
              </div>

              <div className="col-md-6">
                <div className="mt-3">
                  <Label
                    for="outputVolume"
                    className="d-flex justify-content-between align-items-center"
                  >
                    <span>Dispositivo de saída</span>
                    <span className="text-muted">
                      {Math.round(outputVolume * 100)}%
                    </span>
                  </Label>
                  <Input
                    type="range"
                    id="outputVolume"
                    min="0"
                    max="1"
                    step="0.01"
                    value={outputVolume}
                    onChange={handleOutputVolumeChange}
                    className="form-range"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button
          color="primary"
          outline
          onClick={handleRefresh}
          disabled={isLoading}
          className="d-flex align-items-center"
          style={{ gap: '4px' }}
        >
          <FaSync />
          Atualizar Dispositivos
        </Button>
        <Button color="success" onClick={toggle}>
          Salvar
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AudioDeviceSelector;
